import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Skeleton,
} from "@repo/ui";
import { FileText, Download, Loader2 } from "lucide-react";
import type { InvoiceItem } from "@/lib/api/stripe";

interface BillingInvoicesCardProps {
  invoices: InvoiceItem[];
  hasMore: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);
}

function getStatusBadge(status: string | null) {
  switch (status) {
    case "paid":
      return <Badge variant="default">Paid</Badge>;
    case "open":
      return <Badge variant="secondary">Open</Badge>;
    case "void":
      return <Badge variant="outline">Void</Badge>;
    case "uncollectible":
      return <Badge variant="destructive">Uncollectible</Badge>;
    default:
      return <Badge variant="outline">{status || "Unknown"}</Badge>;
  }
}

export function BillingInvoicesCard({
  invoices,
  hasMore,
  isLoading,
  isLoadingMore,
  onLoadMore,
}: BillingInvoicesCardProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10">
            <FileText className="h-5 w-5 text-emerald-600" />
          </div>
          <div>
            <CardTitle>Invoice History</CardTitle>
            <CardDescription>
              View and download your past invoices
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : invoices.length === 0 ? (
          <div className="rounded-xl border border-dashed p-6 text-center space-y-2">
            <FileText className="h-8 w-8 text-muted-foreground mx-auto" />
            <p className="text-sm font-medium text-muted-foreground">
              No invoices yet
            </p>
            <p className="text-xs text-muted-foreground">
              Invoices will appear here after your first payment.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="text-sm">
                        {formatDate(invoice.date)}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {invoice.number || "—"}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {formatAmount(invoice.amount, invoice.currency)}
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell>
                        {invoice.pdfUrl && (
                          <a
                            href={invoice.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {hasMore && (
              <Button
                onClick={onLoadMore}
                disabled={isLoadingMore}
                variant="outline"
                size="sm"
                className="w-full"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
